/**
 * PlayerArea - displays a player's claimed words and score
 *
 * Subscribes only to its own player's data so that memo() actually prevents
 * re-renders when other players' state changes.
 */

import { useState, useEffect, useRef, useMemo, memo, type KeyboardEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tile } from './Tile';
import { PlayerAvatar } from './PlayerAvatar';
import { BotIcon } from '../ui/BotIcon';
import { useGameStore, selectCurrentTurnPlayerId } from '../../stores/gameStore';
import { calculateScore } from '../../utils/game';
import { getPlayerColor } from '../../utils/playerColors';
import type { ClaimedWord } from '../../types/generated';
import clsx from 'clsx';

interface PlayerAreaProps {
  playerId: string;
  isCurrentUser?: boolean;
  onWordClick?: (wordId: string) => void;
}

const EMPTY_WORDS: ClaimedWord[] = [];

export const PlayerArea = memo(function PlayerArea({
  playerId,
  isCurrentUser = false,
  onWordClick,
}: PlayerAreaProps) {
  const player = useGameStore((s) => s.gameState?.players.find((p) => p.id === playerId));
  const rawWords = useGameStore((s) => s.gameState?.playerWords[playerId] ?? EMPTY_WORDS);
  const score = useGameStore((s) => s.gameState?.scores[playerId] ?? 0);
  const isCurrentTurn = useGameStore((s) => selectCurrentTurnPlayerId(s) === playerId);
  // Comma-joined ID strings compare by value, so unrelated selection changes don't re-render
  const selectedKey = useGameStore((s) =>
    rawWords.filter((w) => s.selectedWordsToSteal.has(w.id)).map((w) => w.id).join(',')
  );
  const recentKey = useGameStore((s) =>
    rawWords.filter((w) => s.recentlyClaimedWordIds.has(w.id)).map((w) => w.id).join(',')
  );
  const selectedIds = useMemo(() => new Set(selectedKey ? selectedKey.split(',') : []), [selectedKey]);
  const recentIds = useMemo(() => new Set(recentKey ? recentKey.split(',') : []), [recentKey]);

  // Sort words by length (longest first) for better visual layout
  const words = useMemo(() => [...rawWords].sort((a, b) => b.word.length - a.word.length), [rawWords]);

  // Track score changes for floating indicator (positive and negative)
  const prevScoreRef = useRef<number>(score);
  const [scoreChange, setScoreChange] = useState<{ value: number; isPositive: boolean } | null>(null);

  useEffect(() => {
    const prevScore = prevScoreRef.current;
    prevScoreRef.current = score;
    if (score !== prevScore) {
      const diff = score - prevScore;
      setScoreChange({ value: Math.abs(diff), isPositive: diff > 0 });
      const timer = setTimeout(() => setScoreChange(null), 1500);
      return () => clearTimeout(timer);
    }
  }, [score]);

  if (!player) return null;

  // Get the player's assigned color from the server
  const playerColor = getPlayerColor(player.colorIndex);
  const isDisconnected = !player.isAI && !player.isConnected;

  const handleWordKeyDown = (e: KeyboardEvent<HTMLDivElement>, wordId: string) => {
    if (!onWordClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onWordClick(wordId);
    }
  };

  return (
    <div
      className={clsx(
        'player-card p-1.5 sm:p-2 transition-all duration-300 min-w-0',
        isCurrentUser && 'player-card--current',
        `border-l-4 ${playerColor.border}`,
        isCurrentTurn && 'ring-2 ring-amber-400 ring-offset-2 ring-offset-transparent shadow-[0_0_15px_rgba(251,191,36,0.5)]'
      )}
      data-color={playerColor.name}
      data-player-id={playerId}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-0.5 sm:mb-1 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className={clsx('relative shrink-0 transition-opacity', isDisconnected && 'opacity-40 grayscale')}>
            <PlayerAvatar
              name={player.name}
              colorIndex={player.colorIndex}
              size="sm"
              isCurrentUser={isCurrentUser}
              isAI={player.isAI}
            />
            {player.isAI && (
              <div className="absolute -bottom-0.5 -right-0.5 bg-slate-700 dark:bg-slate-600 rounded-full p-0.5">
                <BotIcon size="sm" className="text-white" />
              </div>
            )}
          </div>
          <div className="flex flex-col min-w-0">
            <span
              className={clsx(
                'font-semibold text-sm sm:text-base text-slate-800 dark:text-slate-100 truncate leading-tight',
                isDisconnected && 'opacity-60'
              )}
            >
              {player.name}
              {isCurrentUser && (
                <span className="text-slate-400 dark:text-slate-500 text-xs ml-1">(you)</span>
              )}
            </span>
            <div className="flex items-center gap-1.5 flex-wrap">
              {player.isHost && (
                <span className="text-xs text-yellow-600 dark:text-yellow-400">Host</span>
              )}
              {player.isAI && (
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  AI Lv.{player.aiDifficulty ?? 5}
                </span>
              )}
              {isDisconnected && (
                <span
                  className="inline-flex items-center gap-0.5 text-xs text-slate-500 dark:text-slate-400"
                  title="Disconnected - turns are skipped until they rejoin"
                >
                  <WifiOffIcon />
                  <span>Offline</span>
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="relative shrink-0">
          <motion.span
            key={score}
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            className={clsx('text-sm sm:text-lg font-bold', playerColor.text)}
          >
            {score}
          </motion.span>
          {/* Floating score change indicator */}
          <AnimatePresence>
            {scoreChange !== null && (
              <motion.span
                key={`change-${score}`}
                // x: -50% lives in the motion transform (a Tailwind translate class would be overwritten)
                initial={{ x: '-50%', y: 0, opacity: 1, scale: 0.8 }}
                animate={{ x: '-50%', y: scoreChange.isPositive ? -30 : 30, opacity: 0, scale: 1.2 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.2, ease: 'easeOut' }}
                className={clsx(
                  'absolute left-1/2 top-0 font-bold text-lg pointer-events-none whitespace-nowrap',
                  scoreChange.isPositive ? 'text-green-500' : 'text-red-500'
                )}
              >
                {scoreChange.isPositive ? '+' : '-'}{scoreChange.value}
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Words - wrapping layout */}
      {words.length === 0 ? (
        <div className="text-slate-400 dark:text-slate-500 text-xs sm:text-sm py-1 sm:py-2">No words yet</div>
      ) : (
        <div className="player-words-grid">
          <AnimatePresence mode="popLayout">
            {words.map((word) => {
              const isSelected = selectedIds.has(word.id);
              const isNewlyClaimed = recentIds.has(word.id);
              // Use tiny tiles for long words (6+ letters)
              const useTinyTiles = word.letters.length >= 6;
              const points = calculateScore(word.word.length);

              return (
                <motion.div
                  key={word.id}
                  layout
                  data-word-id={word.id}
                  initial={{ opacity: 0, scale: 0.8, y: -20 }}
                  animate={{
                    opacity: 1,
                    scale: isSelected ? 1.08 : 1,
                    y: 0,
                    boxShadow: isNewlyClaimed
                      ? '0 0 20px 4px rgba(34, 197, 94, 0.5)'
                      : isSelected
                      ? '0 0 20px 6px rgba(59, 130, 246, 0.5), 0 0 35px 10px rgba(59, 130, 246, 0.25)'
                      : '0 0 0 0 rgba(34, 197, 94, 0)'
                  }}
                  exit={{ opacity: 0, scale: 0.8, x: -50 }}
                  transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 30,
                    boxShadow: { duration: 0.3 },
                    scale: { duration: 0.2 }
                  }}
                  className={clsx(
                    'word-chip flex items-center gap-1 p-1 rounded flex-shrink-0 relative',
                    onWordClick && 'touch-target cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50',
                    isSelected && [
                      'bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900/50 dark:to-blue-800/40',
                      'ring-3 ring-blue-500 dark:ring-blue-400',
                      'ring-offset-2 ring-offset-white dark:ring-offset-slate-800',
                      'z-10'
                    ]
                  )}
                  onClick={onWordClick ? () => onWordClick(word.id) : undefined}
                  onKeyDown={onWordClick ? (e) => handleWordKeyDown(e, word.id) : undefined}
                  role={onWordClick ? 'button' : undefined}
                  tabIndex={onWordClick ? 0 : undefined}
                  aria-pressed={onWordClick ? isSelected : undefined}
                  aria-label={`${word.word}, ${points} point${points !== 1 ? 's' : ''}${onWordClick ? (isSelected ? ', selected to steal' : ', select to steal') : ''}`}
                  whileHover={onWordClick ? { scale: isSelected ? 1.08 : 1.02 } : undefined}
                  whileTap={onWordClick ? { scale: 0.95 } : undefined}
                >
                  {/* Selection badge */}
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-2 -right-2 bg-blue-500 dark:bg-blue-400 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shadow-lg z-20"
                      aria-hidden="true"
                    >
                      ✓
                    </motion.div>
                  )}
                  <div className="flex gap-0.5 flex-shrink-0" aria-hidden="true">
                    {word.letters.map((letter, idx) => (
                      <Tile key={`${word.id}-${idx}`} letter={letter} small={!useTinyTiles} tiny={useTinyTiles} />
                    ))}
                  </div>
                  <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap" aria-hidden="true">
                    +{points}
                  </span>
                  {word.stolenFrom && word.stolenFrom.length > 0 && (
                    <span className="text-xs text-red-400 ml-1 flex-shrink-0" title="Stolen" aria-hidden="true">
                      ⚡
                    </span>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
});

function WifiOffIcon() {
  return (
    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M8.5 16.5a5 5 0 017 0M5 13a10 10 0 015.17-2.8M19 13a10 10 0 00-2.5-1.8M2 9.5a15 15 0 014.1-2.6M22 9.5A15 15 0 0011 5.05M12 20h.01" />
    </svg>
  );
}
